import "../JSONTools/JSONformatter.css";
import "./TextUtilities.css";
import ToolHeader from "../../Components/ToolHeader";
import { Row, Col } from "react-bootstrap";
import { useState, useEffect, useMemo, useRef } from "react";
import { WhitespaceTrimAPI } from "../../Services/APIService";

import FormatterPanel from "../../HomePage/FormatterPanel";
import { InputHeaderButtons, OutputHeaderButtons } from "../JSONTools/JSONToolsHeaderButtons";

const defaultOptions = {
    TRIM_EACH_LINE: false,
    COLLAPSE_SPACES: false,
    REMOVE_EMPTY_LINES: true,
    TRIM: true,
    REMOVE_LINE_BREAKS: false,
    REMOVE_ALL_WHITESPACE: false
};

const optionList = [
    {
        key: "TRIM_EACH_LINE",
        title: "Trim each line",
        desc: "Strip leading/trailing spaces per line"
    },
    {
        key: "COLLAPSE_SPACES",
        title: "Collapse spaces",
        desc: "Multiple spaces -> single space"
    },
    {
        key: "REMOVE_EMPTY_LINES",
        title: "Remove empty lines",
        desc: "Drop blank lines"
    },
    {
        key: "TRIM",
        title: "Trim start/end",
        desc: "Trim entire text"
    },
    {
        key: "REMOVE_LINE_BREAKS",
        title: "Remove line breaks",
        desc: "Join all lines into one"
    },
    {
        key: "REMOVE_ALL_WHITESPACE",
        title: "Remove ALL whitespace",
        desc: "Strip everything (overrides others)"
    }
];

function WhiteSpaceTrim() {
    const [text, setText] = useState("");
    const [result, setresult] = useState("");
    const [processingTimeMs, setProcessingTimeMs] = useState(0);
    const [apiError, setApiError] = useState("");
    const [options, setOptions] = useState(defaultOptions);
    const whitespaceRequestId = useRef(0);
    const toolFav = { toolName: "Whitespace Cleaner", Iconpath: "/FileCodeRaw.svg" };

    const selectedTypes = useMemo(() => (
        Object.entries(options)
            .filter(([, enabled]) => enabled)
            .map(([type]) => type)
    ), [options]);

    

    useEffect(() => {
        const requestId = whitespaceRequestId.current + 1;
        whitespaceRequestId.current = requestId;

        if (!text) {
            setresult("");
            setProcessingTimeMs(0);
            setApiError("");
            return;
        }

        const timeoutId = setTimeout(async () => {
            try {
                const response = await WhitespaceTrimAPI({
                    input: text,
                    types: selectedTypes
                });

                if (requestId === whitespaceRequestId.current) {
                    setresult(response.data.output);
                    setProcessingTimeMs(response.data.processingTimeMs);
                    setApiError("");
                }
            } catch (error) {
                if (requestId === whitespaceRequestId.current) {
                    setApiError(error.response?.data?.message || error.message);
                }
            }
        }, 250);

        return () => clearTimeout(timeoutId);
    }, [text, selectedTypes]);

    

    const toggleOption = (key) => {
        setOptions((prev) => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const resetOptions = () => {
        setOptions(defaultOptions);
    };

    const clearInput = () => {
        setText("");
        setresult("");
        setProcessingTimeMs(0);
        setApiError("");
    };

    const lineCount = text ? text.split("\n").length : 0;
    const resultLineCount = result ? result.split("\n").length : 0;

    return (
        <>
            <Col>
                <ToolHeader title="Whitespace Cleaner" description="Clean up spacing, tabs, and line breaks" toolFav={toolFav} />

                <Row className='Formatter whitespace-trim-row'>
                    <Col xs={12} xl={2} className="whitespace-options-column">
                        <div className="options-card">
                            <div className="options-header">Options</div>

                            <div className="options-body">
                                {optionList.map((item) => (
                                    <label key={item.key} className="option-row">
                                        <input
                                            type="checkbox"
                                            checked={options[item.key]}
                                            onChange={() => toggleOption(item.key)}
                                        />

                                        <div className="option-content">
                                            <div className="option-title">{item.title}</div>
                                            <div className="option-desc">{item.desc}</div>
                                        </div>
                                    </label>
                                ))}
                            </div>

                            <div className="options-footer">
                                <button
                                    type="button"
                                    className="reset-btn"
                                    onClick={resetOptions}
                                >
                                    Reset
                                </button>
                            </div>
                        </div>
                    </Col>
                    <Col xs={12} xl={5} className="whitespace-formatter-column">
                        <FormatterPanel
                            title="Paste text with messy whitespace"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            placeholder="Enter text here..."
                            lineCount={lineCount}
                            headerButtons={
                                <InputHeaderButtons
                                    onClear={clearInput}
                                    onImport={(fileContent) => setText(fileContent)}
                                    accept=".txt,.json,.csv,.md"
                                />
                            }
                            footerLeft={`Characters : ${text.replaceAll(" ", "").length}`}
                            footerRight={`Size : ${new Blob([text]).size} B`}
                        />
                    </Col>
                    <Col xs={12} xl={5} className="whitespace-formatter-column">
                        <FormatterPanel
                            title="Cleaned text will appear here"
                            value={result ?? ""}
                            placeholder="output will appear here..."
                            lineCount={resultLineCount}
                            readOnly
                            headerButtons={
                                <OutputHeaderButtons
                                    outputText={result ?? ""}
                                    fileName="cleaned-text.txt"
                                    mimeType="text/plain;charset=utf-8"
                                />
                            }
                            footerLeft={`Characters : ${result.replaceAll(" ", "").length}`}
                            footerRight={apiError ? `Error : ${apiError}` : `API Time : ${processingTimeMs} ms`}
                        />
                    </Col>
                </Row>
            </Col >
        </>
    );
}

export default WhiteSpaceTrim;
